import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useGame } from '../context/GameContext';
import { NoirBackground, Button, GameCard, Pill } from '../components/GameUI';
import { ArrowRight, MessageCircle } from 'lucide-react';

const GameRound = () => {
  const { players, round, setGameState } = useGame();
  const active = players.filter(p => !p.isEliminated);

  const starter = useMemo(() => {
    if (round !== 1) return null;
    const eligible = active.filter(p => p.role !== 'mrwhite');
    const idx = Math.floor(Math.random() * eligible.length);
    return eligible[idx];
  }, [round]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-dvh flex flex-col items-center justify-center p-6 pb-32 relative select-none">
      <NoirBackground />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md space-y-6 relative z-10">
        <div>
          <Pill color="red" className="mb-3">Round {round}</Pill>
          <h1 className="font-typewriter text-5xl text-[#E0DDD5] leading-none">Interrogation</h1>
          <div className="red-line w-full mt-3" />
        </div>

        {/* Random starter callout - only Round 1 */}
        {starter && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, type: 'spring' }}
          >
            <GameCard className="text-center space-y-2 border-[#EF4444]/20">
              <div className="font-mono text-[#5A5A5A] text-xs uppercase tracking-[0.3em]">Starts First</div>
              <div className="red-line w-8 mx-auto" />
              <div className="font-typewriter text-3xl text-[#EF4444]" data-testid="round-starter-name">
                {starter.name}
              </div>
            </GameCard>
          </motion.div>
        )}

        <GameCard className="text-center space-y-3">
          <MessageCircle size={22} className="mx-auto text-[#EF4444]" />
          <div className="text-[#E0DDD5] font-bold text-sm uppercase tracking-wider">Clue Phase</div>
          <div className="text-[#5A5A5A] text-sm font-mono">Each suspect gives ONE word clue</div>
        </GameCard>

        <div className="space-y-2">
          <div className="font-mono text-[#5A5A5A] text-xs uppercase tracking-[0.2em]">Remaining Suspects</div>
          <div className="grid grid-cols-2 gap-2">
            {active.map(p => (
              <div key={p.id} className={`bg-gradient-to-b from-[#161616] to-[#111] border rounded p-3 text-center ${
                starter && p.id === starter.id ? 'border-[#EF4444]/40' : 'border-[#222]'
              }`}>
                <div className={`font-mono font-bold text-sm ${
                  starter && p.id === starter.id ? 'text-[#EF4444]' : 'text-[#E0DDD5]'
                }`}>{p.name}</div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
      <div className="fixed bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-[#080808] via-[#0A0A0A]/95 to-transparent z-20">
        <div className="max-w-md mx-auto">
          <Button data-testid="proceed-to-voting-btn" color="red" onClick={() => setGameState('vote')}>
            <span className="flex items-center justify-center gap-2">Proceed to Verdict <ArrowRight size={18} /></span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GameRound;
